import { Controller } from "@hotwired/stimulus"
import { trackBrowserEvent } from "../lib/browser_event_tracker"
import { beginLessonSearchLoadingProgress } from "../lib/lesson_search_loading"

const DEBOUNCE_MS = 100
const SLOW_LOOKUP_MS = 300
export const SUGGESTION_LINK_ACTIONS = [
  "click->search-suggestions#beginNavigation",
  "click->modal-filters#beginSuggestionNavigation",
].join(" ")

export default class extends Controller {
  static targets = ["input", "panel", "list", "status", "defaultContent"]
  static values = { url: String }

  connect() {
    this.selectedIndex = -1
    this.hasTrackedQueryTelemetry = false
    this.boundKeydown = this.keydown.bind(this)
    this.inputTarget.addEventListener("keydown", this.boundKeydown, true)
    this.boundSubmit = () => this.close()
    this.element.addEventListener("submit", this.boundSubmit)
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    this.request?.abort()
    this.inputTarget.removeEventListener("keydown", this.boundKeydown, true)
    this.element.removeEventListener("submit", this.boundSubmit)
  }

  focus() {
    this.beginInteraction("focus")
    this.beginLoading("Loading recent searches…")
    this.scheduleLoad(0)
  }

  input() {
    this.beginInteraction("input")
    this.beginLoading("Updating suggestions…")
    this.scheduleLoad(DEBOUNCE_MS)
  }

  blur() {
    window.setTimeout(() => {
      if (!this.panelTarget.contains(document.activeElement)) this.close()
    }, 0)
  }

  scheduleLoad(delay) {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = window.setTimeout(() => this.load(), delay)
  }

  async load() {
    if (!this.hasUrlValue) {
      this.finishLoading()
      return
    }

    this.request?.abort()
    this.request = new AbortController()
    const request = this.request
    const query = this.inputTarget.value.trim()
    const interactionStartedAt = this.interactionStartedAt || performance.now()
    const trigger = this.interactionTrigger || "focus"
    const requestStartedAt = performance.now()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      if (query) url.searchParams.set("query", query)
      const response = await fetch(url, {
        signal: request.signal,
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
      if (request !== this.request) return
      if (!response.ok || response.redirected) {
        this.close()
        return
      }

      const payload = await response.json()
      if (request !== this.request) return
      const renderStartedAt = performance.now()
      const results = payload.results || []
      this.render(results, query)
      const completedAt = performance.now()
      this.trackLatency({
        query,
        trigger,
        resultCount: results.length,
        totalDurationMs: completedAt - interactionStartedAt,
        debounceDurationMs: requestStartedAt - interactionStartedAt,
        requestDurationMs: renderStartedAt - requestStartedAt,
        renderDurationMs: completedAt - renderStartedAt,
        serverDurationMs: this.serverDuration(response),
      })
    } catch (error) {
      if (error.name !== "AbortError") this.close()
    } finally {
      if (request === this.request) {
        this.finishLoading()
        this.request = null
      }
    }
  }

  beginInteraction(trigger) {
    this.request?.abort()
    this.request = null
    this.interactionStartedAt = performance.now()
    this.interactionTrigger = trigger
  }

  beginLoading(message) {
    this.open()
    this.panelTarget.classList.add("is-loading")
    this.panelTarget.setAttribute("aria-busy", "true")
    this.statusTarget.textContent = message
  }

  finishLoading() {
    this.panelTarget.classList.remove("is-loading")
    this.panelTarget.removeAttribute("aria-busy")
  }

  serverDuration(response) {
    const timing = response.headers?.get?.("Server-Timing") || ""
    const match = timing.match(/search_suggestions;dur=([\d.]+)/)
    if (!match) return null

    const duration = Number(match[1])
    return Number.isFinite(duration) ? Math.round(duration) : null
  }

  trackLatency({ query, trigger, resultCount, totalDurationMs, debounceDurationMs,
    requestDurationMs, renderDurationMs, serverDurationMs }) {
    if (!query) return

    const roundedTotalDuration = Math.round(totalDurationMs)
    if (this.hasTrackedQueryTelemetry && roundedTotalDuration < SLOW_LOOKUP_MS) return

    this.hasTrackedQueryTelemetry = true
    trackBrowserEvent({
      event_class: "SearchSuggestionsLoaded",
      trigger,
      query_length: query.length,
      word_count: query.split(/\s+/).filter(Boolean).length,
      result_count: resultCount,
      total_duration_ms: roundedTotalDuration,
      debounce_duration_ms: Math.max(0, Math.round(debounceDurationMs)),
      request_duration_ms: Math.max(0, Math.round(requestDurationMs)),
      render_duration_ms: Math.max(0, Math.round(renderDurationMs)),
      server_duration_ms: serverDurationMs,
      viewport_width: window.innerWidth,
      connection_type: navigator.connection?.effectiveType || null,
    })
  }

  render(results, query) {
    if (results.length === 0 && !query) {
      this.close()
      return
    }

    this.open()
    this.listTarget.replaceChildren()
    this.selectedIndex = -1

    results.forEach((result) => this.listTarget.append(this.resultRow(result)))
    if (results.length > 0) {
      this.statusTarget.textContent = `${results.length} ${results.length === 1 ? "suggestion" : "suggestions"}`
    } else if (query) {
      this.statusTarget.textContent = `Press Search to look for “${query}”.`
    } else {
      this.statusTarget.textContent = "No recent searches yet. Start with a technique, position or instructor."
    }
  }

  resultRow(result) {
    const item = document.createElement("li")
    item.className = "search-suggestion"
    item.dataset.searchSuggestionResult = ""

    const link = document.createElement("a")
    link.className = "search-suggestion__link"
    link.href = result.url
    // The app-level shell is intentionally a second, independent loading path.
    // A cold search modal can be restored from Turbo's page cache; if its nested
    // modal controller has not reconnected by the time the user taps, the link
    // must still cover the page immediately rather than leaving the suggestion
    // list exposed until the response arrives.
    link.dataset.action = SUGGESTION_LINK_ACTIONS
    link.dataset.lessonSearchLoadingQuery = result.query
    link.dataset.lessonLibraryLoadingLabel = result.query
    link.dataset.turboPrefetch = "false"
    link.append(this.icon(result.kind), this.copy(result))

    const fill = document.createElement("button")
    fill.type = "button"
    fill.className = "search-suggestion__fill"
    fill.dataset.action = "search-suggestions#fill"
    fill.dataset.query = result.fill_query
    fill.setAttribute("aria-label", `Use ${result.query} in the search field`)
    fill.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 18 6 6m0 0v6m0-6h6"/></svg>'

    item.append(link, fill)
    return item
  }

  // The body-level navigation controller cannot always be reached through a
  // Stimulus action from inside a Turbo-restored, nested modal scope. Use the
  // already-connected suggestions controller to reveal the same app-level
  // shell directly; its body controller still owns before-cache cleanup.
  beginNavigation(event) {
    if (!this.primaryNavigationClick(event)) return

    const shell = document.getElementById("lesson_library_navigation_loading")
    if (!shell) return

    const label = event.currentTarget?.dataset?.lessonLibraryLoadingLabel?.trim() || "All lessons"
    const queryLabel = shell.querySelector("[data-lesson-search-loading-query]")
    if (queryLabel) queryLabel.textContent = label

    shell.classList.remove("hidden")
    shell.classList.add("is-active")
    shell.setAttribute("aria-hidden", "false")
    beginLessonSearchLoadingProgress(shell)
  }

  primaryNavigationClick(event) {
    if (event.button !== undefined && event.button !== 0) return false
    return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
  }

  icon(kind) {
    const icon = document.createElement("span")
    icon.className = "search-suggestion__icon"
    icon.setAttribute("aria-hidden", "true")
    icon.innerHTML = kind === "history"
      ? '<svg viewBox="0 0 24 24"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/></svg>'
      : '<svg viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 4 4"/></svg>'
    return icon
  }

  copy(result) {
    const copy = document.createElement("span")
    copy.className = "search-suggestion__copy"
    const label = document.createElement("strong")
    label.textContent = result.query
    const meta = document.createElement("small")
    meta.textContent = result.meta
    copy.append(label, meta)
    return copy
  }

  fill(event) {
    this.inputTarget.value = event.currentTarget.dataset.query || ""
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.focus({ preventScroll: true })
  }

  keydown(event) {
    if (this.panelTarget.classList.contains("hidden")) return
    const rows = [...this.listTarget.querySelectorAll("[data-search-suggestion-result]")]

    if (event.key === "Escape") {
      event.preventDefault()
      event.stopImmediatePropagation()
      this.close()
      return
    }
    if (!rows.length || !["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return

    if (event.key === "Enter" && this.selectedIndex >= 0) {
      event.preventDefault()
      event.stopImmediatePropagation()
      rows[this.selectedIndex].querySelector("a")?.click()
      return
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      event.stopImmediatePropagation()
      const direction = event.key === "ArrowDown" ? 1 : -1
      this.selectedIndex = (this.selectedIndex + direction + rows.length) % rows.length
      rows.forEach((row, index) => row.classList.toggle("is-active", index === this.selectedIndex))
      rows[this.selectedIndex].scrollIntoView({ block: "nearest" })
    }
  }

  open() {
    this.panelTarget.classList.remove("hidden")
    this.defaultContentTargets.forEach((target) => target.classList.add("hidden"))
    this.element.closest(".video-filters")?.classList.add("is-suggesting")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  close() {
    this.finishLoading()
    this.panelTarget.classList.add("hidden")
    this.defaultContentTargets.forEach((target) => target.classList.remove("hidden"))
    this.element.closest(".video-filters")?.classList.remove("is-suggesting")
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.selectedIndex = -1
  }
}
